/**
 * Importación de pacientes desde otro sistema (Excel o CSV): reconoce las columnas por su encabezado,
 * corrige lo que Excel suele estropear (ceros iniciales de la cédula y del celular, fechas como número)
 * y valida cada fila con las mismas reglas del registro manual.
 */
import { norm } from "./texto.ts";
import { cedulaValida, celularExtranjero, celularValido, correoValido, telefonoValido } from "./identificacion.ts";

export const CAMPOS_IMPORTACION = [
  "apellidos", "nombres", "nombre_completo", "doc", "nacimiento", "edad", "sexo", "telefono", "email", "direccion", "ciudad",
  "ocupacion", "referido_por", "emergencia", "emergencia_telefono", "hc_anterior", "representante", "representante_doc", "representante_telefono", "observaciones",
] as const;
export type CampoImportacion = (typeof CAMPOS_IMPORTACION)[number];

export const NOMBRE_CAMPO_IMPORTACION: Readonly<Record<CampoImportacion, string>> = {
  apellidos: "Apellidos", nombres: "Nombres", nombre_completo: "Nombre completo", doc: "Cédula o pasaporte", nacimiento: "Fecha de nacimiento",
  edad: "Edad", sexo: "Sexo", telefono: "Celular", email: "Correo", direccion: "Dirección", ciudad: "Ciudad", ocupacion: "Ocupación", referido_por: "Referido por", emergencia: "Contacto de emergencia", emergencia_telefono: "Teléfono de emergencia",
  hc_anterior: "N.º de historia anterior", representante: "Representante legal", representante_doc: "Cédula del representante",
  representante_telefono: "Celular del representante", observaciones: "Observaciones",
};

/** Palabras con que suelen venir los encabezados (sin tildes ni mayúsculas). El orden importa: lo más específico primero. */
const SINONIMOS: readonly [CampoImportacion, RegExp][] = [
  ["representante_doc", /^(cedula|ci|documento|identificacion) (del )?(representante|tutor|padre|madre|responsable)/],
  ["representante_telefono", /^(celular|telefono|movil|whatsapp) (del )?(representante|tutor|padre|madre|responsable)/],
  ["emergencia_telefono", /^(telefono|celular|tel|telf)\.? (de )?(emergencia|contacto de emergencia)/],
  ["emergencia", /^(contacto (de )?emergencia|en caso de emergencia|emergencia)/],
  ["representante", /^(representante|tutor|responsable|padre o madre|nombre del representante)/],
  ["hc_anterior", /^(n(ro|o|um|umero)?\.? ?(de )?)?(historia|hc|h\.c\.|ficha|expediente)( clinica)?( anterior)?$|^codigo$|^id paciente$/],
  ["nombre_completo", /^(nombre completo|paciente|nombres y apellidos|apellidos y nombres|nombre del paciente|nombre)$/],
  ["apellidos", /^apellidos?/],
  ["nombres", /^(nombres?|primer nombre)$/],
  ["doc", /^(cedula|c\.?i\.?|ci|dni|documento|identificacion|pasaporte|cedula o pasaporte|n(ro|o|um)?\.? ?(de )?cedula)/],
  ["nacimiento", /^(fecha de nacimiento|f\.? ?nac|nacimiento|fecha nac|cumpleanos|fecha de nac)/],
  ["edad", /^edad/],
  ["sexo", /^(sexo|genero)/],
  ["telefono", /^(celular|telefono|movil|whatsapp|tel|telf|cel)/],
  ["email", /^(correo|e-?mail|mail|correo electronico)/],
  ["direccion", /^(direccion|domicilio)/],
  ["ciudad", /^(ciudad|canton|localidad)/],
  ["ocupacion", /^(ocupacion|profesion|trabajo)/],
  ["referido_por", /^(referido|refirio|referencia|recomendado|quien (lo |la )?(refirio|recomendo)|como nos conocio)/],
  ["observaciones", /^(observaciones?|notas?|comentarios?)/],
];

/** Reconoce qué columna es cada campo; las columnas que no se reconocen se ignoran. */
export function reconocerColumnas(encabezados: string[]): Partial<Record<CampoImportacion, number>> {
  const mapa: Partial<Record<CampoImportacion, number>> = {};
  encabezados.forEach((h, i) => {
    const t = norm(String(h ?? "")).replace(/[°º]/g, "o").replace(/#/g, "no ").replace(/[_:*]/g, " ").replace(/\s+/g, " ").trim();
    if (!t) return;
    const hallado = SINONIMOS.find(([campo, re]) => mapa[campo] === undefined && re.test(t));
    if (hallado) mapa[hallado[0]] = i;
  });
  // con apellidos y nombres por separado no hace falta el nombre completo
  if (mapa.apellidos !== undefined && mapa.nombres !== undefined) delete mapa.nombre_completo;
  return mapa;
}

/* ---------- CSV ---------- */

/** Lee un CSV (separado por coma, punto y coma o tabulador, con comillas) en filas de celdas. */
export function leerCSV(texto: string): string[][] {
  const t = texto.replace(/^﻿/, "");
  const primera = t.split(/\r?\n/, 1)[0] ?? "";
  const cuenta = (c: string) => primera.split(c).length;
  const sep = [";", "\t", ","].reduce((a, b) => (cuenta(b) > cuenta(a) ? b : a), ",");
  const filas: string[][] = [];
  let fila: string[] = [], celda = "", comillas = false;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (comillas) {
      if (ch === '"' && t[i + 1] === '"') { celda += '"'; i++; }
      else if (ch === '"') comillas = false;
      else celda += ch;
    } else if (ch === '"' && celda === "") comillas = true;
    else if (ch === sep) { fila.push(celda); celda = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && t[i + 1] === "\n") i++;
      fila.push(celda); filas.push(fila); fila = []; celda = "";
    } else celda += ch;
  }
  if (celda !== "" || fila.length) { fila.push(celda); filas.push(fila); }
  return filas.filter((f) => f.some((c) => c.trim() !== ""));
}

/* ---------- normalización de valores ---------- */

const hoyISO = () => new Date().toISOString().slice(0, 10);

/** Fecha en ISO desde dd/mm/aaaa, aaaa-mm-dd, dd-mm-aaaa, dd.mm.aaaa o el número de serie de Excel. */
export function fechaImportada(v: string, hoy = hoyISO()): string | null {
  const s = v.trim();
  if (!s) return null;
  let a: number, m: number, d: number;
  let r: RegExpMatchArray | null;
  if (/^\d{4,5}(\.\d+)?$/.test(s) && Number(s) > 366 && Number(s) < 80000) {
    // número de serie de Excel: días desde 1899-12-30
    const f = new Date(Date.UTC(1899, 11, 30) + Math.floor(Number(s)) * 86400000);
    [a, m, d] = [f.getUTCFullYear(), f.getUTCMonth() + 1, f.getUTCDate()];
  } else if ((r = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/))) [a, m, d] = [Number(r[1]), Number(r[2]), Number(r[3])];
  else if ((r = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/))) {
    [d, m, a] = [Number(r[1]), Number(r[2]), Number(r[3])];
    if (a < 100) a += a > Number(hoy.slice(2, 4)) ? 1900 : 2000;
    if (m > 12 && d <= 12) [d, m] = [m, d]; // venía en formato mes/día
  } else return null;
  const f = new Date(Date.UTC(a, m - 1, d));
  if (f.getUTCFullYear() !== a || f.getUTCMonth() !== m - 1 || f.getUTCDate() !== d) return null;
  const iso = f.toISOString().slice(0, 10);
  return iso > hoy || a < 1900 ? null : iso;
}

/** Cédula tal como la deja Excel (sin el cero inicial, con guion o espacios). */
export function docImportado(v: string): { doc: string; tipo: "cedula" | "pasaporte" } {
  const s = v.trim().replace(/\.0$/, "");
  const digitos = s.replace(/[\s-]/g, "");
  if (/^\d{9,10}$/.test(digitos)) {
    const c = digitos.padStart(10, "0");
    if (cedulaValida(c)) return { doc: c, tipo: "cedula" };
    return { doc: digitos, tipo: "cedula" };
  }
  return { doc: s.toUpperCase().replace(/\s/g, ""), tipo: "pasaporte" };
}

/** Celular ecuatoriano desde 0991234567, 991234567, +593 99 123 4567 o 593991234567. */
export function celularImportado(v: string): string {
  if (celularExtranjero(v)) return "+" + v.replace(/\D/g, ""); // de otro país: se respeta tal cual
  let d = v.replace(/\.0$/, "").replace(/\D/g, "");
  if (d.startsWith("593")) d = d.slice(3);
  if (/^9\d{8}$/.test(d) || /^[2-7]\d{7}$/.test(d)) d = "0" + d; // Excel quita el cero (celular o fijo)
  return d;
}

export function sexoImportado(v: string): "F" | "M" | "O" | null {
  const s = norm(v.trim());
  if (!s) return null;
  if (/^(f|fem|femenino|mujer|female)$/.test(s)) return "F";
  if (/^(m|masc|masculino|hombre|varon|male)$/.test(s)) return "M";
  if (/^(o|otro|x|no binario)$/.test(s)) return "O";
  return null;
}

const titulo = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (_, a: string, b: string) => a + b.toUpperCase());

/** Separa un nombre completo: en Ecuador lo usual es «Apellidos Nombres» (dos y dos). */
export function separarNombre(completo: string, orden: "apellidos" | "nombres"): { nombres: string; apellidos: string } {
  const p = completo.trim().split(/\s+/).filter(Boolean);
  if (p.length <= 1) return { nombres: p.join(" "), apellidos: "" };
  const nAp = p.length >= 3 ? 2 : 1;
  if (orden === "apellidos") return { apellidos: p.slice(0, nAp).join(" "), nombres: p.slice(nAp).join(" ") };
  return { nombres: p.slice(0, p.length - nAp).join(" "), apellidos: p.slice(p.length - nAp).join(" ") };
}

export interface PacienteImportado {
  fila: number;
  nombres: string; apellidos: string; doc_tipo: "cedula" | "pasaporte"; doc: string; nacimiento: string | null; sexo: "F" | "M" | "O";
  telefono: string; email: string; direccion: string; ciudad: string;
  filiacion: Record<string, string>; representante: { nombre: string; doc: string; telefono: string } | null;
}
export interface FilaRevisada { paciente: PacienteImportado; errores: string[]; avisos: string[] }

/** Convierte una fila de la hoja en un paciente, con sus errores (no se importa) y avisos (se importa, pero conviene revisar). */
export function revisarFila(celdas: string[], mapa: Partial<Record<CampoImportacion, number>>, n: number, o: { orden: "apellidos" | "nombres"; hoy?: string; menoresSinRepresentante?: boolean } = { orden: "apellidos" }): FilaRevisada {
  const hoy = o.hoy ?? hoyISO();
  const v = (k: CampoImportacion) => (mapa[k] === undefined ? "" : String(celdas[mapa[k]!] ?? "").trim());
  const errores: string[] = [], avisos: string[] = [];
  let { nombres, apellidos } = { nombres: titulo(v("nombres")), apellidos: titulo(v("apellidos")) };
  if (!nombres && !apellidos && v("nombre_completo")) ({ nombres, apellidos } = separarNombre(titulo(v("nombre_completo")), o.orden));
  if (!nombres) errores.push("Falta el nombre.");
  if (nombres.length > 80 || apellidos.length > 80) errores.push("El nombre es demasiado largo.");

  const docTexto = v("doc");
  const { doc, tipo } = docTexto ? docImportado(docTexto) : { doc: "", tipo: "cedula" as const };
  if (doc && tipo === "cedula" && !cedulaValida(doc)) errores.push(`La cédula ${doc} no es válida.`);
  if (doc.length > 20) errores.push("La identificación es demasiado larga.");
  if (!doc) avisos.push("Sin cédula: no se podrá detectar si ya existe.");

  let nacimiento: string | null = null;
  if (v("nacimiento")) {
    nacimiento = fechaImportada(v("nacimiento"), hoy);
    if (!nacimiento) errores.push(`No se entiende la fecha de nacimiento «${v("nacimiento")}» (usa día/mes/año).`);
  } else if (/^\d{1,3}$/.test(v("edad")) && Number(v("edad")) <= 110) {
    nacimiento = `${Number(hoy.slice(0, 4)) - Number(v("edad"))}-01-01`;
    avisos.push("Solo tenía la edad: la fecha de nacimiento queda aproximada (1 de enero).");
  }

  const sx = sexoImportado(v("sexo"));
  if (v("sexo") && !sx) avisos.push(`Sexo «${v("sexo")}» no reconocido: queda como Otro.`);

  let telefono = v("telefono") ? celularImportado(v("telefono")) : "";
  if (telefono && !celularValido(telefono)) { avisos.push(`El celular «${v("telefono")}» no es válido: no se importa (sin él no le llegarán recordatorios).`); telefono = ""; }
  let email = v("email").toLowerCase();
  if (email && !correoValido(email)) { avisos.push(`El correo «${v("email")}» no es válido: no se importa.`); email = ""; }

  const filiacion: Record<string, string> = {};
  if (v("ocupacion")) filiacion.ocupacion = v("ocupacion").slice(0, 80);
  if (v("referido_por")) filiacion.referido_por = v("referido_por").slice(0, 120);
  if (v("emergencia")) filiacion.emergencia = v("emergencia").slice(0, 120);
  if (v("emergencia_telefono")) {
    const te = celularImportado(v("emergencia_telefono"));
    if (telefonoValido(te)) filiacion.emergencia_telefono = te;
    else avisos.push(`El teléfono de emergencia «${v("emergencia_telefono")}» no es válido: no se importa.`);
  }
  if (v("hc_anterior")) filiacion.hc_anterior = v("hc_anterior").replace(/\.0$/, "").slice(0, 30);
  if (v("observaciones")) filiacion.observaciones = v("observaciones").slice(0, 500);

  let representante: PacienteImportado["representante"] = null;
  if (v("representante")) {
    const rd = v("representante_doc") ? docImportado(v("representante_doc")).doc : "";
    if (rd && /^\d{10}$/.test(rd) && !cedulaValida(rd)) errores.push(`La cédula del representante ${rd} no es válida.`);
    const rt = v("representante_telefono") ? celularImportado(v("representante_telefono")) : "";
    representante = { nombre: titulo(v("representante")).slice(0, 120), doc: rd, telefono: celularValido(rt) ? rt : "" };
  }
  if (nacimiento) {
    const [a, m, d] = nacimiento.split("-").map(Number), [ha, hm, hd] = hoy.split("-").map(Number);
    const edad = ha - a - (hm < m || (hm === m && hd < d) ? 1 : 0);
    if (edad < 18 && !representante) {
      // con la opción «importar menores sin representante» se importan y queda la marca para completarlo en su ficha
      if (o.menoresSinRepresentante) { filiacion.representante_pendiente = "1"; avisos.push("Menor de edad sin representante: se importa y falta agregar su representante legal en su ficha."); }
      else errores.push("Es menor de edad y falta su representante legal (agrega una columna «Representante»).");
    }
  }
  return {
    paciente: {
      fila: n, nombres, apellidos, doc_tipo: tipo, doc, nacimiento, sexo: sx ?? "O", telefono, email,
      direccion: v("direccion").slice(0, 200), ciudad: titulo(v("ciudad")).slice(0, 80), filiacion, representante,
    },
    errores, avisos,
  };
}

/** Revisa toda la hoja: primera fila = encabezados; marca las cédulas repetidas dentro del mismo archivo. */
export function revisarHoja(filas: string[][], o: { orden: "apellidos" | "nombres"; hoy?: string; mapa?: Partial<Record<CampoImportacion, number>>; menoresSinRepresentante?: boolean } = { orden: "apellidos" }) {
  const [enc = [], ...datos] = filas;
  const mapa = o.mapa ?? reconocerColumnas(enc);
  const faltan: string[] = [];
  if (mapa.nombres === undefined && mapa.nombre_completo === undefined) faltan.push("No encontré la columna del nombre (por ejemplo «Nombres» o «Nombre completo»).");
  const vistos = new Map<string, number>();
  const revisadas = datos.map((f, i) => {
    const r = revisarFila(f, mapa, i + 2, o);
    const d = r.paciente.doc;
    if (d) {
      if (vistos.has(d)) r.errores.push(`La identificación ${d} ya está en la fila ${vistos.get(d)} del archivo.`);
      else vistos.set(d, i + 2);
    }
    return r;
  });
  return { encabezados: enc, mapa, faltan, filas: revisadas };
}
